import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AppSidebar } from '@/components/AppSidebar';
import { AppWatermark } from '@/components/AppWatermark';
import { UtilityBar } from '@/components/UtilityBar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { visibleSections } from '@/lib/navigation';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import { RAIL_COLLAPSE } from '@/lib/collapse';
import { RightRailContext, type RightRailContextValue } from '@/lib/rightRailContext';
import { cn } from '@/lib/utils';
import { actingOrgUnitTypeOf } from '@/lib/accessRole';

/**
 * THE APPLICATION SHELL: banner, sidebar, utility bar, content, opt-in right rail.
 *
 * ─── Why a sidebar and not the top bar P1a shipped ───────────────────────────────────
 * A top nav has a fixed horizontal budget, so it cannot grow nesting, section grouping or
 * a second tier without becoming a dropdown maze. The org section needs all three. It is
 * also the single most recognisable shape of a generated admin panel.
 *
 * ─── NAV IS FILTERED, NOT DISABLED ───────────────────────────────────────────────────
 * Unlike an action button, a nav item the user has never had raises no "where did it go"
 * question — and a nav item that leads straight to a /forbidden redirect is worse than its
 * absence. Action buttons INSIDE a page are disabled with a tooltip instead, because there
 * the control's absence is what confuses.
 *
 * ⚠️ None of this is enforcement. The API is.
 *
 * ─── Three invariants the P1a suite depends on, and their mechanisms ─────────────────
 *  1. `getByRole('link', { name })` resolves to EXACTLY ONE element. The desktop <aside>
 *     is always mounted — Tailwind is never loaded into jsdom, so `hidden md:block` does
 *     not affect the accessibility tree there — and the mobile Sheet is UNMOUNTED while
 *     closed, so there is never a second copy.
 *  2. A nav row's accessible name does not depend on the collapse state: the label is
 *     `sr-only` in the rail, never removed.
 *  3. Identity renders exactly once, always. See `OfficerIdentity`.
 *
 * `{children ?? <Outlet />}` supports both call styles: `routes/index.tsx` uses it as a
 * LAYOUT ROUTE element, and `AppShell.test.tsx` passes children directly.
 */
export const AppShell = ({ children }: { children?: ReactNode }) => {
  const { officer } = useAuth();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railHost, setRailHost] = useState<HTMLElement | null>(null);
  const [railCount, setRailCount] = useState(0);
  const { pathname } = useLocation();

  /*
   * The drawer must not survive a route change — and keying on the PATHNAME rather than
   * on a click handler is what makes imperative navigation (the ⌘K palette) close it too.
   *
   * Adjusted DURING RENDER, React's documented pattern, rather than in an effect: an
   * effect would paint one frame with the drawer still over the new page. `useServerTable`
   * uses the same shape for its search box, for the same reason.
   */
  const [lastPathname, setLastPathname] = useState(pathname);
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setDrawerOpen(false);
  }

  /*
   * ⚠️ [C1c §7] The org unit TYPE as well as the role — see `visibleSections`.
   *
   * ⚠️ [UX1 §8] AND THE DEPLOYMENT'S SETTING, so the `+` beside Manage Arrangements answers the
   * same rule the route does. NO LOADING BRANCH HERE, deliberately: a nav action that appears a
   * beat late is fine, whereas `RequireDivision` bouncing a beat early is not — and the default
   * while it resolves is the one that hides the action.
   */
  const { nodalsMayAuthor } = useArrangementAuthoringSetting();
  /* ⚠️ [C5b §5 · AUDIT-FLAG] And the officer's own flags, so System's two flag-gated rows — Audit log
     and Usage analytics — each answer what their own route does. */
  /* [C5c §11] The level the officer ACTS at — the posting unless its unit is elevated. */
  const actingType = actingOrgUnitTypeOf(officer);
  const sections = useMemo(
    () => visibleSections(officer?.accessRole, undefined, actingType, nodalsMayAuthor, officer?.flags),
    [officer?.accessRole, actingType, nodalsMayAuthor, officer?.flags],
  );

  const setOccupied = useCallback(
    (occupied: boolean) => setRailCount((count) => Math.max(0, count + (occupied ? 1 : -1))),
    [],
  );
  const rail = useMemo<RightRailContextValue>(() => ({ host: railHost, setOccupied }), [railHost, setOccupied]);

  return (
    <RightRailContext value={rail}>
      {/* h-screen + overflow-hidden: <main> scrolls, the window never does. */}
      <div className="flex h-screen w-full flex-col overflow-hidden bg-background text-foreground">
        {/*
         * FR-5.8.12 — persistent, above everything, on every authenticated screen. A
         * SIBLING of the shell row rather than a child of <main>, so it cannot be
         * scrolled out of view.
         */}

        <div className="flex min-h-0 w-full flex-1 overflow-hidden">
          <aside
            className={cn(
              'hidden shrink-0 overflow-hidden border-r border-border transition-[width] duration-200 ease-in-out md:block',
              collapsed ? 'md:w-14' : 'md:w-64',
            )}
          >
            {/* No `onNavigate`: the desktop sidebar cannot close anything, structurally. */}
            <AppSidebar sections={sections} officer={officer} collapsed={collapsed} navLabel="Main" />
          </aside>

          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              {/*
               * `aria-label="Navigation"`, NOT "Main". `queryByRole` throws on multiple
               * matches, and `App.test.tsx` queries the Main landmark by name.
               */}
              <AppSidebar
                sections={sections}
                officer={officer}
                navLabel="Navigation"
                onNavigate={() => setDrawerOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col">
            <UtilityBar
              collapsed={collapsed}
              onToggleCollapse={toggleCollapsed}
              onOpenDrawer={() => setDrawerOpen(true)}
            />

            {/*
              THE RIGHT-RAIL AFFORDANCE: the content row is already two columns.

              ⚠️ [amended UX-FINAL] `@container/shell` — NAMED, and the name is what makes it
              safe. `<main>`'s own `@container` is UNNAMED and nearer to every page, so an
              unnamed `@min-[880px]:` written inside a page still resolves to `<main>` exactly
              as before; only `…/shell:` variants reach past it to this row.

              ⚠️ BELOW THE THRESHOLD THIS ROW IS A COLUMN, not a narrower row. That is what
              turns the rail into a bar on the fold with no `position` at all — and while the
              rail is unoccupied (`hidden`), a one-item flex row and a one-item flex column
              are the same box, so this is a zero-pixel change on every screen shipping today.

              ⚠️ Same containment note as `<main>`: `container-type` implies `contain: layout`,
              so this box is a containing block for any `position: fixed` descendant. Nothing
              in the shell has one; every overlay portals to `<body>`.
            */}
            {/*
              ⚠️ [C1b §7] `relative isolate` IS FOR THE WATERMARK, AND BOTH WORDS EARN THEIR
              PLACE. `container-type: inline-size` already computes `contain: layout`, which
              makes this box a containing block for absolute descendants AND a stacking
              context — so `absolute` positions against it and `-z-10` resolves inside it
              today with neither word present. They are declared anyway, for `tokens.ts`'s
              "declared rather than derived" reason: the guarantee must not depend on
              `@container/shell` surviving a later edit. Adding them changes nothing now, and
              nothing is trapped by the stacking context because every overlay in this app
              portals to `<body>`.
            */}
            <div className="@container/shell relative isolate flex min-h-0 flex-1 @max-[1304px]/shell:flex-col">
              {/*
                ⚠️ ROUTE-GATED, AND A ROUTE IS STATE RATHER THAN A WIDTH. §8 permits a state
                branch to change what renders and forbids a width branch from doing so
                (`vitest.setup.ts` answers `matchMedia` `matches: false` for ever, so a width
                branch takes the narrow path in every test, silently). The WIDTH half of §7 is
                a container query inside the component.

                ⚠️ `=== '/dashboard'` RATHER THAN `startsWith`. `/dashboard/tiles/:tileId` is a
                drill-through — a list, not a landing — and a prefix test would be a third
                place that decides what "the dashboard" means.
              */}
              {pathname === '/dashboard' && <AppWatermark />}
              {/*
                ⚠️ [P4-UX] `@container` IS LOAD-BEARING AND IS THE ONE ANCHOR FOR EVERY
                CONTENT BREAKPOINT IN THE APP. Read `lib/layout.ts` before touching it.

                Tailwind's breakpoints measure the WINDOW; every split lives in here, which
                is 304px narrower (256 sidebar + 48 gutter). `container-type: inline-size`
                queries this element's content box, so a page can say `@min-[880px]:` and
                mean 880px of usable width — and be right when the sidebar collapses to its
                56px rail, which no media query can be.

                ⚠️ It also makes `<main>` a containing block for any `position: fixed`
                descendant. Nothing in the app has one today; every overlay portals to
                `<body>`. A future `fixed` child inside a page would be positioned against
                this box rather than the viewport.

                ⚠️ `mx-auto` + `max-w` is the 2560 answer: at 2K the extra width becomes
                gutters, not a 1400px-wide form field. It is a no-op below 1920 + the shell,
                so it cannot regress the floor.
              */}
              {/*
                ⚠️ [D4.4 §6] `overscroll-x-none` HERE AS WELL AS ON THE ROOT, and both are
                needed. This is the element a horizontal trackpad gesture actually starts
                over — it is the app's one scroll container — so without it the gesture
                reaches the root by propagation and the root's own rule is the only thing
                left standing. Stopping it at the container it began in is the narrower and
                more reliable of the two. See `styles/index.css` for what neither can prove.
              */}
              <main className="@container mx-auto min-h-0 w-full min-w-0 max-w-[1920px] flex-1 overflow-auto overscroll-x-none px-4 py-6 sm:px-6">
                {children ?? <Outlet />}
              </main>
              {/*
                ⚠️ [amended UX-FINAL] THE `xl` THRESHOLD IS GONE, AND WITH IT THE HOLE.
                It meant anything portalled in here VANISHED below 1280px with no fallback —
                the org-panel defect waiting to happen a second time, in a component built to
                be reused. Below `RAIL_THRESHOLD_PX` the rail is now the same summary bar
                `TwoColumnLayout`'s secondary column becomes, so the app has ONE collapse
                behaviour rather than two. See `lib/collapse.ts`.

                ⚠️ THE `hidden` ATTRIBUTE STAYS, AND IT IS NOW LOAD-BEARING FOR THE UNIT SUITE.
                §8 bans it for CONTENT, because jsdom loads no stylesheet and a class keeps
                content addressable. Here it does the opposite job and is the right tool: no
                page uses `RightRail`, so on every web test this subtree would otherwise hold a
                "Details" toggle for content that does not exist. The attribute keeps it out of
                the accessibility tree, and preflight's `[hidden]{display:none!important}`
                still wins over the container query.
              */}
              <aside
                aria-label="Details"
                hidden={railCount === 0}
                className="w-full shrink-0 border-border @max-[1304px]/shell:border-t @min-[1304px]/shell:w-[336px] @min-[1304px]/shell:overflow-auto @min-[1304px]/shell:border-l"
              >
                <CollapsiblePanel label="Details" placement={RAIL_COLLAPSE}>
                  {/*
                    ⚠️ THE PORTAL TARGET IS THIS DIV, NEVER THE `<aside>`. React must not own
                    children in a node it also portals into, and the bar above is a React child.

                    ⚠️ `@container` so anything portalled in measures the 336px RAIL. Without
                    it the first consumer's `@min-[880px]:` would resolve against `/shell` —
                    the whole row — and fire at a width the rail has never had.
                  */}
                  <div ref={setRailHost} className="@container min-w-0" />
                </CollapsiblePanel>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </RightRailContext>
  );
};
